<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Sample emails</span>
    </template>

    <template #actions>
      <router-link :to="`/sales-plays/${playId}`" class="backLink">
        <AppIcon name="arrow_right" :size="14" class="backArrow" />
        Back to play
      </router-link>
    </template>

    <div v-if="loading" class="pageLoading">Loading…</div>
    <div v-else-if="loadError" class="errorBanner">{{ loadError }}</div>

    <template v-else>
      <header class="pageHead">
        <h1 class="title">Sample emails for {{ playName }}</h1>
        <p class="sub">
          Reference emails the agent draws on for tone and structure when writing outreach.
          Add a handful of strong examples — short, specific, and in your voice work best.
        </p>
      </header>

      <div v-if="pageError" class="errorBanner">{{ pageError }}</div>

      <div v-if="!drafts.length" class="emptyState">
        <p>No sample emails yet.</p>
        <button v-if="!isFlock" class="btn primary" type="button" @click="addEmail">+ Add sample email</button>
      </div>

      <ol v-else class="emailList">
        <li v-for="(d, i) in drafts" :key="d.localKey" class="emailCard">
          <div class="cardHead">
            <span class="emailIndex">{{ i + 1 }}</span>
            <span v-if="!d.serverId" class="newBadge">New</span>
            <span v-else-if="isDirty(d)" class="dirtyBadge">Unsaved</span>
            <span class="cardSpacer" />
            <button
              v-if="!isFlock"
              class="iconBtn"
              type="button"
              :title="d.serverId ? 'Delete sample email' : 'Discard'"
              :disabled="d.saving || d.deleting"
              @click="removeEmail(i)"
            >
              <AppIcon name="close" :size="16" />
            </button>
          </div>

          <div class="formField">
            <textarea
              class="formTextarea"
              :readonly="isFlock"
              v-model="d.body"
              placeholder="Paste an example email here…"
              rows="10"
              maxlength="20000"
            />
          </div>

          <div v-if="d.error" class="cardError">{{ d.error }}</div>

          <div v-if="!isFlock" class="cardActions">
            <button
              v-if="d.serverId && isDirty(d)"
              class="btn secondary"
              type="button"
              :disabled="d.saving"
              @click="revertEmail(i)"
            >
              Revert
            </button>
            <button
              class="btn primary"
              type="button"
              :disabled="!isDirty(d) || !d.body.trim() || d.saving"
              @click="saveEmail(i)"
            >
              {{ d.saving ? 'Saving…' : d.serverId ? 'Save' : 'Create' }}
            </button>
          </div>
        </li>
      </ol>

      <button v-if="drafts.length && !isFlock" class="btn addRow" type="button" @click="addEmail">
        + Add sample email
      </button>
    </template>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { useChatScope } from '../composables/useChatScope'
import { usePlayEngine, type PlayEngine } from '../composables/usePlayEngine'

interface SampleEmailApi {
  id: string
  body: string
  created_at?: string
}

interface DraftEmail {
  localKey: number
  serverId: string | null
  body: string
  baseline: string
  saving: boolean
  deleting: boolean
  error: string
}

const route = useRoute()
const { setScope, clearScope } = useChatScope()

const playId = computed(() => String(route.params.id || ''))

const playName = ref('')
const play = ref<{ engine?: PlayEngine } | null>(null)
const { isFlock } = usePlayEngine(play)
const drafts = ref<DraftEmail[]>([])
const loading = ref(true)
const loadError = ref('')
const pageError = ref('')

let nextLocalKey = 1

function toDraft(e: SampleEmailApi): DraftEmail {
  return {
    localKey: nextLocalKey++,
    serverId: e.id,
    body: e.body || '',
    baseline: e.body || '',
    saving: false,
    deleting: false,
    error: '',
  }
}

function isDirty(d: DraftEmail): boolean {
  return d.body !== d.baseline
}

function addEmail() {
  drafts.value.push({
    localKey: nextLocalKey++,
    serverId: null,
    body: '',
    baseline: '',
    saving: false,
    deleting: false,
    error: '',
  })
}

function revertEmail(i: number) {
  const d = drafts.value[i]
  if (!d) return
  d.body = d.baseline
  d.error = ''
}

async function removeEmail(i: number) {
  const d = drafts.value[i]
  if (!d) return
  if (!d.serverId) {
    drafts.value.splice(i, 1)
    return
  }
  if (!window.confirm('Delete this sample email?')) return
  d.deleting = true
  d.error = ''
  pageError.value = ''
  try {
    await apiFetch(
      `/sales-plays/${encodeURIComponent(playId.value)}/sample-emails/${encodeURIComponent(d.serverId)}`,
      { method: 'DELETE' },
    )
    drafts.value.splice(i, 1)
  } catch (e: any) {
    d.deleting = false
    d.error = e?.message || 'Delete failed'
  }
}

async function saveEmail(i: number) {
  const d = drafts.value[i]
  if (!d) return
  const body = d.body.trim()
  if (!body) {
    d.error = 'Body cannot be empty'
    return
  }
  d.saving = true
  d.error = ''
  pageError.value = ''
  try {
    if (d.serverId) {
      const resp = await apiFetch(
        `/sales-plays/${encodeURIComponent(playId.value)}/sample-emails/${encodeURIComponent(d.serverId)}`,
        { method: 'PATCH', body: { body } },
      )
      const saved = (resp?.sample_email || {}) as SampleEmailApi
      d.body = saved.body ?? body
      d.baseline = d.body
    } else {
      const resp = await apiFetch(
        `/sales-plays/${encodeURIComponent(playId.value)}/sample-emails`,
        { method: 'POST', body: { body } },
      )
      const saved = (resp?.sample_email || {}) as SampleEmailApi
      d.serverId = saved.id || null
      d.body = saved.body ?? body
      d.baseline = d.body
    }
  } catch (e: any) {
    d.error = e?.message || 'Save failed'
  } finally {
    d.saving = false
  }
}

async function reload() {
  loading.value = true
  loadError.value = ''
  pageError.value = ''
  try {
    const resp = await apiFetch(`/sales-plays/${encodeURIComponent(playId.value)}`)
    const detail = resp?.play || null
    play.value = detail
    playName.value = detail?.name || ''
    const sequenceAction = (detail?.actions || []).find((a: any) => a.action_type === 'sequence')
    const samples = (sequenceAction?.definition?.sample_emails || []) as SampleEmailApi[]
    drafts.value = samples.map(toDraft)
  } catch (e: any) {
    loadError.value = e?.message || 'Failed to load sample emails'
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  setScope({
    scopeType: 'sales_play',
    scopeSubjectId: playId.value,
    pageContext: { sales_play_id: playId.value, page_type: 'sales_play_emails' },
  })
  await reload()
})

onBeforeUnmount(() => {
  clearScope()
})
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: var(--lh-tight);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.backLink {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color var(--duration-fast) var(--ease-out);
}
.backLink:hover { color: var(--color-accent); }
.backArrow { transform: rotate(180deg); }

.pageLoading { color: var(--color-text-tertiary); font-size: var(--fs-sm); }

.errorBanner {
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-4);
  background: var(--color-danger-soft);
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);
  font-size: var(--fs-sm);
  color: var(--color-danger);
}

.pageHead {
  max-width: 720px;
  margin-bottom: var(--space-5);
}
.title {
  margin: 0 0 var(--space-2);
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
}
.sub {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
}

.emptyState {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-3);
  max-width: 720px;
  padding: var(--space-5);
  background: var(--color-bg-surface);
  border: 1px dashed var(--color-border-subtle);
  border-radius: var(--radius-lg);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}

.emailList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  max-width: 720px;
}

.emailCard {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-neu-out-cream-sm);
}

.cardHead {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.emailIndex {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: var(--radius-full);
  background: var(--color-bg-subtle);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-text-secondary);
  flex-shrink: 0;
}

.newBadge,
.dirtyBadge {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
}
.newBadge {
  background: var(--color-bg-subtle);
  color: var(--color-text-secondary);
}
.dirtyBadge {
  background: var(--color-bg-subtle);
  color: var(--color-accent);
}

.cardSpacer { flex: 1; }

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  color: var(--color-text-tertiary);
  transition:
    background var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out);
}
.iconBtn:hover { background: var(--color-bg-subtle); color: var(--color-danger); }
.iconBtn:disabled { opacity: 0.5; cursor: not-allowed; }

.formField {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.formTextarea {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  background: var(--color-bg-app);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--fs-sm);
  line-height: var(--lh-normal);
  resize: vertical;
  min-height: 180px;
}
.formTextarea:focus { outline: none; border-color: var(--color-accent); }

.cardError {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-danger);
}

.cardActions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

.addRow {
  align-self: flex-start;
  margin-top: var(--space-4);
}
</style>
